import { useEffect, useState, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Stack,
    Avatar,
    Divider,
    IconButton,
    keyframes
} from "@mui/material";

// Standard Icons
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

// Modern Timeline Icons
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SoupKitchenIcon from '@mui/icons-material/SoupKitchen';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { useParams, useNavigate } from "react-router-dom";
import API from "../api/api";
import GlobalSkeleton from "../components/GlobalSkeleton";

// =========================
// MODERN ANIMATIONS
// =========================
const pulseLive = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  70% { box-shadow: 0 0 0 8px rgba(239, 68, 68, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
`;

const pulseActiveStep = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(248, 195, 50, 0.4); }
  70% { box-shadow: 0 0 0 12px rgba(248, 195, 50, 0); }
  100% { box-shadow: 0 0 0 0 rgba(248, 195, 50, 0); }
`;

const floatIcon = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

export default function LiveOrderPage() {
    const { orderId } = useParams();
    const navigate = useNavigate();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    // =========================
    // PROGRESS CALCULATOR (0 to 4)
    // =========================
    const getActiveStep = (status) => {
        if (!status) return 0;
        if (status === "DELIVERED") return 4; 
        if (["PICKED_UP", "OUT_FOR_DELIVERY"].includes(status)) return 3; 
        if (["ASSIGNED", "AT_VENDOR"].includes(status)) return 2; 
        if (["ACCEPTED", "READY"].includes(status)) return 1; 
        return 0; // PLACED
    };

    const activeStep = getActiveStep(order?.status);

    const timelineSteps = [
        { label: "Order Placed", icon: <ReceiptLongIcon fontSize="small" /> },
        { label: "Preparing", icon: <SoupKitchenIcon fontSize="small" /> },
        { label: "Partner Assigned", icon: <AssignmentIndIcon fontSize="small" /> },
        { label: "On the Way", icon: <TwoWheelerIcon fontSize="small" /> },
        { label: "Delivered", icon: <HomeIcon fontSize="small" /> }
    ];

    // =========================
    // FETCH LIVE ORDER
    // =========================
    const fetchOrder = useCallback(async () => {
        try {
            const res = await API.get(`/order/live/${orderId}`);

            if (res.data && res.data.success === false) {
                console.log("Live order fetch failed:", res.data.error);
                return;
            }

            setOrder(res.data);
        } catch (err) {
            console.log("Live order fetch failed");
        } finally {
            setLoading(false);
        }
    }, [orderId]);

    // =========================
    // AUTO POLLING
    // =========================
    // Inside useEffect
  useEffect(() => {
      fetchOrder();
      
      const interval = setInterval(() => {
          // Only fetch if the user is actively looking at this tab
          if (!document.hidden) {
              fetchOrder();
          }
      }, 15000); // Increased interval from 5s to 15s

      return () => clearInterval(interval);
  }, [fetchOrder]);

    // =========================
    // LOADING UI
    // =========================
    if (loading) {
        return (
            <Box sx={{ minHeight: "100vh", bgcolor: "#FFFDF8", pb: 10 }}>
                <Box
                    sx={{
                        bgcolor: "#F8D548",
                        pt: 1, pb: 2, px: 3,
                        minHeight: "70px",
                        mb: 3,
                        borderRadius: "0 0 24px 24px"
                    }}
                />
                <Box sx={{ mt: 1 }}>
                    <GlobalSkeleton type="list" />
                </Box>
            </Box>
        );
    }

    // =========================
    // MAIN UI
    // =========================
    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#FFFDF8",
                pb: 10
            }}
        >
            {/* HEADER */}
            <Box
                sx={{
                    bgcolor: "#F8D548",
                    pt: 2, // Adjusted to match About page padding
                    pb: 3, // Adjusted to match About page padding
                    px: 3,
                    mb: 3,
                    borderRadius: "0 0 30px 30px" // Adjusted curve to match About page
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 0 }}>
                    <IconButton
                        onClick={() => navigate("/home")}
                        sx={{ color: "#4A332E", bgcolor: "rgba(255,255,255,0.15)" }}
                    >
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Box>
                        <Typography variant="h6" fontWeight="800" sx={{ color: "#4A332E", lineHeight: 1.2 }}>
                            Live Order Tracking
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6E655E", fontWeight: 600 }}>
                            Order #{order?.order_id}
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* STATUS CARD */}
            <Paper
                elevation={0}
                sx={{
                    mx: 2,
                    mt: 2,
                    p: 2.5,
                    bgcolor: "#FFFFFF",
                    borderRadius: "24px",
                    border: "1px solid #ECE5DA",
                    boxShadow: "0 4px 16px rgba(58, 44, 35, 0.04)"
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                        sx={{
                            bgcolor: order?.status === "DELIVERED" ? "#DDECC8" : "#FFF6DE",
                            color: order?.status === "DELIVERED" ? "#6A9E3A" : "#F29D17",
                            width: 56,
                            height: 56,
                            borderRadius: "16px"
                        }}
                    >
                        {order?.status === "DELIVERED" ? <CheckCircleIcon fontSize="medium" /> : <DeliveryDiningIcon fontSize="medium" />}
                    </Avatar>
                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={{ color: "#6E655E", fontWeight: 600, mb: 0.5 }}
                        >
                            Current Status
                        </Typography>
                        <Typography
                            variant="h6"
                            fontWeight="900"
                            sx={{ color: "#3A2C23", lineHeight: 1.1 }}
                        >
                            {order?.status
                                ?.toLowerCase()
                                .split("_")
                                .map(word =>
                                    word.charAt(0).toUpperCase() + word.slice(1)
                                )
                                .join(" ")
                            }
                        </Typography>
                    </Box>
                </Stack>
            </Paper>

            {/* MODERN ANIMATED TIMELINE */}
            <Paper
                elevation={0}
                sx={{
                    mx: 2,
                    mt: 2,
                    p: 3,
                    bgcolor: "#FFFFFF",
                    borderRadius: "24px",
                    border: "1px solid #ECE5DA",
                    boxShadow: "0 4px 16px rgba(58, 44, 35, 0.04)",
                    overflow: "hidden"
                }}
            >
                {/* Live Indicator Header */}
                {order?.status !== "DELIVERED" && (
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ mb: 4 }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                bgcolor: "#ef4444",
                                borderRadius: "50%",
                                animation: `${pulseLive} 2s infinite`
                            }}
                        />
                        <Typography variant="subtitle2" fontWeight="800" sx={{ color: "#6E655E" }}>
                            Live: Your order is in progress
                        </Typography>
                    </Stack>
                )}

                {/* Timeline Track & Nodes */}
                <Box sx={{ position: "relative", mb: 4, mt: 1, px: { xs: 0, sm: 1 } }}>
                    
                    {/* Background Gray Line */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: { xs: "14px", sm: "16px" }, 
                            left: "12%",
                            right: "12%",
                            height: "4px",
                            bgcolor: "#F2EEE8",
                            borderRadius: "4px",
                            zIndex: 0
                        }}
                    />

                    {/* Active Yellow Line */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: { xs: "14px", sm: "16px" }, 
                            left: "12%",
                            width: `${(activeStep / (timelineSteps.length - 1)) * 76}%`,
                            height: "4px",
                            bgcolor: "#F8C332",
                            borderRadius: "4px",
                            transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                            zIndex: 1
                        }}
                    />

                    {/* Timeline Nodes */}
                    <Stack direction="row" justifyContent="space-between" sx={{ position: "relative", zIndex: 2 }}>
                        {timelineSteps.map((step, index) => {
                            const isCompleted = index < activeStep || order?.status === "DELIVERED";
                            const isActive = index === activeStep && order?.status !== "DELIVERED";
                            const isPending = index > activeStep;
                    
                            return (
                                <Stack key={index} alignItems="center" sx={{ width: "20%", px: { xs: 0.2, sm: 0.5 } }}>
                                    <Box
                                        sx={{
                                            width: { xs: 28, sm: 36 },
                                            height: { xs: 28, sm: 36 },
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            borderRadius: "50%",
                                            transition: "all 0.4s ease",
                                            bgcolor: isCompleted ? "#6A9E3A" : isActive ? "#F8C332" : "#FFFFFF",
                                            color: isCompleted || isActive ? "#FFFFFF" : "#A59D96",
                                            border: isPending ? "2px solid #ECE5DA" : "2px solid transparent",
                                            animation: isActive ? `${pulseActiveStep} 2s infinite` : "none",
                                            mb: { xs: 0.5, sm: 1.5 },
                                            "& svg": {
                                                fontSize: { xs: "0.9rem", sm: "1.25rem" }, 
                                                animation: isActive && index === 3 ? `${floatIcon} 1.5s infinite` : "none" 
                                            }
                                        }}
                                    >
                                        {isCompleted ? <CheckCircleIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }} /> : step.icon}
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        fontWeight={isActive || isCompleted ? "800" : "600"}
                                        sx={{
                                            color: isActive ? "#3A2C23" : isCompleted ? "#6A9E3A" : "#A59D96",
                                            textAlign: "center",
                                            lineHeight: 1.1,
                                            fontSize: { xs: "0.55rem", sm: "0.75rem" },
                                            wordWrap: "break-word"
                                        }}
                                    >
                                        {step.label}
                                    </Typography>
                                </Stack>
                            );
                        })}
                    </Stack>
                </Box>
            </Paper>

            {/* DELIVERY BOY */}
            {order?.delivery_boy && (
                <Paper
                    elevation={0}
                    sx={{
                        mx: 2,
                        mt: 2,
                        p: 2.5,
                        bgcolor: "#FFFFFF",
                        borderRadius: "24px",
                        border: "1px solid #ECE5DA",
                        boxShadow: "0 4px 16px rgba(58, 44, 35, 0.04)"
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        fontWeight="800"
                        sx={{ mb: 2, color: "#3A2C23" }}
                    >
                        Delivery Partner
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                            sx={{
                                bgcolor: "#eadbb7",
                                color: "#3A2C23",
                                border: '2px solid #FFFFFF',
                                boxShadow: '0 4px 10px rgba(58, 44, 35, 0.05)'
                            }}
                        >
                            <ShoppingBagIcon fontSize="small" />
                        </Avatar>
                        <Box>
                            <Typography fontWeight="800" sx={{ color: "#2F2A25" }}>
                                {order.delivery_boy.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#6E655E", fontWeight: 500 }}>
                                {order.delivery_boy.phone}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            )}

            {/* ITEMS & BILL BREAKDOWN */}
            <Paper
                elevation={0}
                sx={{
                    mx: 2,
                    mt: 2,
                    p: 3,
                    bgcolor: "#FFFFFF",
                    borderRadius: "24px",
                    border: "1px solid #ECE5DA",
                    boxShadow: "0 4px 16px rgba(58, 44, 35, 0.04)"
                }}
            >
                <Typography
                    variant="subtitle1"
                    fontWeight="800"
                    sx={{ mb: 2, color: "#3A2C23" }}
                >
                    Ordered Items
                </Typography>
                
                <Stack spacing={2}>
                    {order?.items?.map((item, index) => {
                        const isNonVeg = item.category 
                            ? item.category.toLowerCase().includes('non') 
                            : (item.name.toLowerCase().includes('chicken') || 
                               item.name.toLowerCase().includes('fish') || 
                               item.name.toLowerCase().includes('biriyani') || 
                               item.name.toLowerCase().includes('mutton') || 
                               item.name.toLowerCase().includes('egg'));

                        const iconColor = isNonVeg ? '#E85D3F' : '#6A9E3A';

                        return (
                            <Stack
                                key={index}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                spacing={2}
                            >
                                {/* LEFT SIDE: Icon and Text */}
                                <Stack 
                                    direction="row" 
                                    spacing={1.5} 
                                    alignItems="flex-start"
                                    sx={{ flex: 1, minWidth: 0 }}
                                >
                                    <Box
                                        sx={{
                                            width: 14, height: 14, flexShrink: 0, mt: 0.4,
                                            border: `1.5px solid ${iconColor}`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '3px'
                                        }}
                                    >
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: iconColor }} />
                                    </Box>

                                    <Typography 
                                        fontWeight="700" 
                                        sx={{ color: "#2F2A25", wordBreak: 'break-word' }}
                                    >
                                        <span style={{ color: '#F29D17', fontWeight: 900 }}>{item.quantity} x</span> {item.name}
                                    </Typography>
                                </Stack>
                                
                                {/* RIGHT SIDE: Price Logic */}
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ flexShrink: 0 }} 
                                >
                                    {item.display_price > item.price && (
                                        <Typography
                                            sx={{
                                                textDecoration: "line-through",
                                                color: "#999",
                                                fontSize: 12,
                                                fontWeight: 600
                                            }}
                                        >
                                            ₹{item.display_price * item.quantity}
                                        </Typography>
                                    )}

                                    <Typography
                                        fontWeight="800"
                                        sx={{
                                            color: item.display_price > item.price ? "#FF4B2B" : "#2F2A25"
                                        }}
                                    >
                                        ₹{item.price * item.quantity}
                                    </Typography>
                                </Stack>
                            </Stack>
                        );
                    })}
                </Stack>

                <Divider sx={{ mt: 2.5, mb: 2.5, borderColor: '#ECE5DA', borderStyle: 'dashed' }} />

                {/* BILL BREAKDOWN */}
                {(() => {
                    const itemTotal = order?.total_amount || 0;
                    // 🚫 GST DISABLED — Mealno is not GST registered, so no GST is charged.
                    // const gst = order?.gst || 0;
                    const couponDiscount = order?.discount_amount || 0;
                    const additionalDiscount = order?.additional_discount || 0;

                    return (
                        <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" sx={{ color: "#6E655E", fontWeight: 600 }}>Item Total</Typography>
                                <Typography variant="body2" sx={{ color: "#2F2A25", fontWeight: 800 }}>
                                    ₹{itemTotal}
                                </Typography>
                            </Stack>

                            {additionalDiscount > 0 && (
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: "#16A34A", fontWeight: 600 }}>Additional Discount</Typography>
                                    <Typography variant="body2" sx={{ color: "#16A34A", fontWeight: 800 }}>
                                        -₹{additionalDiscount}
                                    </Typography>
                                </Stack>
                            )}

                            {couponDiscount > 0 && (
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ color: "#16A34A", fontWeight: 600 }}>Coupon Discount</Typography>
                                    <Typography variant="body2" sx={{ color: "#16A34A", fontWeight: 800 }}>
                                        -₹{couponDiscount}
                                    </Typography>
                                </Stack>
                            )}

                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" sx={{ color: "#6E655E", fontWeight: 600 }}>Delivery Charge</Typography>
                                <Typography variant="body2" sx={{ color: "#16A34A", fontWeight: 800 }}>FREE</Typography>
                            </Stack>

                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" sx={{ color: "#6E655E", fontWeight: 600 }}>Platform Fee</Typography>
                                <Typography variant="body2" sx={{ color: "#16A34A", fontWeight: 800 }}>FREE</Typography>
                            </Stack>

                            {/* 🚫 GST DISABLED — Mealno is not GST registered, so no GST is charged. */}
                            {/*
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" sx={{ color: "#6E655E", fontWeight: 600 }}>GST (5%)</Typography>
                                <Typography variant="body2" sx={{ color: "#2F2A25", fontWeight: 800 }}>
                                    ₹{gst}
                                </Typography>
                            </Stack>
                            */}
                        </Stack>
                    );
                })()}

                <Divider sx={{ mt: 2.5, mb: 2.5, borderColor: '#ECE5DA' }} />

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography fontWeight="800" sx={{ color: "#3A2C23", fontSize: '1.1rem' }}>
                        Total Bill
                    </Typography>
                    <Typography
                        fontWeight="900"
                        sx={{ color: "#2F2A25", fontSize: '1.2rem' }}
                    >
                        ₹{order?.final_amount}
                    </Typography>
                </Stack>
            </Paper>
        </Box>
    );
}